import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { BackendLead, BackendLeadListResponse, LeadListParams } from "../types/lead.types";
import { mapBackendLead, toCreateLeadRequest, toUpdateLeadStatusRequest } from "./leads.mapper";

type BackendSuccessResponse<T> = {
	success: true;
	data: T;
};

function unwrapData<T>(responseData: BackendSuccessResponse<T> | T): T {
	if (responseData && typeof responseData === "object" && "success" in responseData) {
		return (responseData as BackendSuccessResponse<T>).data;
	}

	return responseData as T;
}

export async function getLeads(params?: LeadListParams) {
	const response = await apiClient.get<BackendLeadListResponse>(endpoints.leads.list, {
		params,
	});

	return {
		items: response.data.data.items.map(mapBackendLead),
		meta: response.data.data.meta,
	};
}

export async function createLead(
	name: string,
	phone: string,
	email: string,
	source: BackendLead["source"],
	statusId?: string,
	buildingId?: string,
	responsibleUserId?: string
) {
	const response = await apiClient.post<BackendSuccessResponse<BackendLead> | BackendLead>(
		endpoints.leads.create,
		toCreateLeadRequest(name, phone, email, source, statusId, buildingId, responsibleUserId)
	);

	return mapBackendLead(unwrapData(response.data));
}

export async function updateLead(
	id: string,
	data: Partial<
		Pick<
			BackendLead,
			"fullName" | "phone" | "email" | "source" | "buildingId" | "statusId" | "responsibleUserId"
		>
	>
) {
	const response = await apiClient.patch<BackendSuccessResponse<BackendLead> | BackendLead>(
		endpoints.leads.detail(id),
		data
	);
	return mapBackendLead(unwrapData(response.data));
}

export async function updateLeadStatus(id: string, statusId: string, responsibleUserId?: string) {
	const response = await apiClient.patch<BackendSuccessResponse<BackendLead> | BackendLead>(
		endpoints.leads.status(id),
		toUpdateLeadStatusRequest(statusId, responsibleUserId)
	);

	return mapBackendLead(unwrapData(response.data));
}

export async function deleteLead(id: string) {
	await apiClient.delete(endpoints.leads.detail(id));
}

export async function getLeadsApiErrorMessage(error: unknown, fallback: string) {
	if (typeof error !== "object" || error === null || !("response" in error)) {
		return fallback;
	}

	const response = (error as { response?: { data?: { error?: { message?: unknown } } } }).response;
	return typeof response?.data?.error?.message === "string"
		? response.data.error.message
		: fallback;
}
